import * as React from 'react';
import i18n from 'i18next';
import { action, computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import MetaInfoService from '../../../services/MetaInfoService/MetaInfoService';
import { Picker } from './Picker';
import { WidgetOptions } from '@sprinklr/stories/widget/WidgetOptions';
import { SortOption } from '@sprinklr/stories/widget/types';
import { WidgetMetric } from 'utils/Widget/Metric/WidgetMetric';
import { AnalyticsEngine, AnalyticsSortOrder } from '@sprinklr/stories/analytics/AnalyticsRequest';
import { WidgetGroupBy } from 'utils/Widget/GroupBy/WidgetGroupBy';
import styled, { css } from '@sprinklr/space-kit/lib/designSystem';
import { reset } from '@sprinklr/space-kit/lib/designSystem/style';
import Flexbox from '@sprinklr/space-kit/lib/Flexbox/Flexbox';
import VirtualSelect from 'components/_UI/VirtualSelect/VirtualSelect';
import Icon, { StyledIcon } from '@sprinklr/space-kit/lib/Icon/Icon';

interface PickerClientSortProps {
    metaInfoService?: MetaInfoService;
    engine: AnalyticsEngine;
    requestMetrics?: WidgetMetric[];
    groupBys?: WidgetGroupBy[];
    widgetOptions: WidgetOptions;
    onChangeSortBy?(heading: any, origOrder?: AnalyticsSortOrder): void;
    onChangeSortByOrder?(order?: string): void;
    placeholder?: string;
}

const StyledSortCombo = styled(Flexbox)<{ disabled?: boolean }>`
    ${reset}
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
            pointer-events: none;
        `}
    .Select-control {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        width: 184px;
    }
    .ReactSelect__single-value {
        font-size: 12px;
    }
`;

const StyledSortOrder = styled(Flexbox)<{ disabled?: boolean }>`
    ${({ theme }) =>
        css`
            width: 36px;
            height: 40px;
            border-top: 1px solid ${theme && theme.sortCombo.border};
            border-right: 1px solid ${theme && theme.sortCombo.border};
            border-bottom: 1px solid ${theme && theme.sortCombo.border};
            ${StyledIcon} svg {
                fill: ${theme && theme.sortCombo.icon};
            }
            :hover {
                ${StyledIcon} svg {
                    fill: ${theme && theme.sortCombo.hover.icon};
                }
            }
        `};
`;

type SortComboOption = { label: string; value: string; type: SortOption };

class PickerClientSort extends Picker<PickerClientSortProps, any> {
    @computed get status() {
        return this.getStatus(this.props.metaInfoService, this.props.engine, null, this.options);
    }

    @computed
    get groupByOptions(): SortComboOption[] {
        const { groupBys } = this.props;
        // Only date groupBys should be sort options
        const filteredGroupBys = groupBys.filter(groupBy => groupBy.isDate);
        return filteredGroupBys.map(groupBy => {
            return {
                label: groupBy.dimensionLabel,
                value: groupBy.heading,
                type: 'label' as SortOption,
            };
        });
    }

    get metricOptions(): SortComboOption[] {
        const { requestMetrics } = this.props;
        return requestMetrics.map(metric => {
            return {
                label: metric.metricLabel,
                value: metric.heading,
                type: 'metric' as SortOption,
            };
        });
    }

    @computed
    get options(): SortComboOption[] {
        const result: SortComboOption[] = [
            {
                label: i18n.t('Auto'),
                value: 'auto',
                type: 'auto',
            },
            ...this.metricOptions,
            ...this.groupByOptions,
        ];
        return result.filter(item => item.label && item.value);
    }

    @computed get sortOrder(): string {
        return this.props.widgetOptions.sortDirection === 'DESC'
            ? 'sortdescending'
            : 'sortascending';
    }

    @computed
    get selectedSortBy(): string {
        const { widgetOptions, requestMetrics, groupBys } = this.props;
        switch (widgetOptions.sort) {
            case 'auto':
                return 'auto';
            case 'label':
                return widgetOptions.sortHeading || groupBys[0].heading;
            case 'metric':
                return widgetOptions.sortHeading || requestMetrics[0].heading;
        }
    }

    @computed
    get orderPlacement(): string {
        return this.props.widgetOptions.sort === 'auto' ? 'none' : 'after';
    }

    render() {
        const { placeholder } = this.props;

        const orderIcon = (
            <StyledSortOrder data-id='sort-order' middle center noGrow>
                <Icon
                    toolTip={this.sortOrder}
                    value={this.sortOrder}
                    size='m'
                    onClick={!!this.props.onChangeSortByOrder ? this.onChangeOrder : undefined}
                />
            </StyledSortOrder>
        );
        return (
            <StyledSortCombo data-id='sort-combo' noGrow>
                <VirtualSelect
                    multi={false}
                    clearable={true}
                    closeOnSelect={true}
                    isLoading={this.status.loading}
                    name='sort'
                    value={this.options.find(option => option.value === this.selectedSortBy)}
                    options={this.options}
                    placeholder={placeholder}
                    onChange={this.onChangeHeading}
                    labelKey='label'
                    scrollOnOpen
                />
                {this.orderPlacement === 'after' && orderIcon}
            </StyledSortCombo>
        );
    }

    @action
    private onChangeHeading = (result: SortComboOption): void => {
        const { requestMetrics, onChangeSortBy, widgetOptions } = this.props;

        if (!result) {
            result = {
                value: 'none',
                type: 'none',
                label: 'none',
            };
        }
        widgetOptions.sort = result.type;
        widgetOptions.sortHeading = result.value;
        widgetOptions.requestSort = true;

        if (result.value === 'none') {
            onChangeSortBy(result.value, this.props.widgetOptions.sortDirection);
        } else if (result.value === 'auto') {
            onChangeSortBy(requestMetrics[0].heading, 'DESC');
        } else {
            const found =
                this.options &&
                this.options.find(option => {
                    return option === result;
                });

            if (found) {
                onChangeSortBy(found.value, this.props.widgetOptions.sortDirection);
            }
        }
    };

    @action
    private onChangeOrder = (): void => {
        const { widgetOptions, onChangeSortByOrder } = this.props;
        if (!widgetOptions.requestSort) {
            widgetOptions.requestSort = true;
        }

        const newOrder = widgetOptions.sortDirection === 'DESC' ? 'ASC' : 'DESC';
        if (onChangeSortByOrder) {
            onChangeSortByOrder(newOrder);
            widgetOptions.sortDirection = newOrder;
        }
    };
}

export const formatHeading = (heading: string, aggregateFunction: string): string => {
    return !heading.includes('_CHANGE') &&
        (aggregateFunction === 'CHANGE' || aggregateFunction === 'PERCENTAGE_CHANGE')
        ? `${heading}_${aggregateFunction}`
        : heading;
};

export default inject('metaInfoService')(observer(PickerClientSort));
